import * as React from 'react'
import { toast as toastify } from 'react-toastify'
import { breakpoints } from '../../shared/breakpoints'
import { media } from '../../shared/css-snippets'
import { styled, css } from '../../theme/styled'

const ToastMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const ToastAction = styled.span`
  color: ${(p) => p.theme.av8.secondaryColor};
  text-decoration: underline;
  ${() => media.mobileDown} {
    align-self: flex-start;
  }
`

interface ToastActionConfig {
  label: string
  onClick?: () => void
}

interface ToastOptions {
  action?: ToastActionConfig
  shouldDeduplicate?: boolean
}

const bodyParser = ({
  message,
  action
}: {
  message: string
  action: ToastActionConfig
}): string | React.ReactNode => {
  if (!action) {
    return message
  }
  return (
    <ToastMessage>
      <span>{message}</span>
      <ToastAction
        onClick={(_e) => {
          action.onClick?.()
        }}
      >
        {action.label}
      </ToastAction>
    </ToastMessage>
  )
}

const optionsParser = (params: { shouldDeduplicate: boolean; message?: string }) => {
  if (!params.shouldDeduplicate) return undefined
  return {
    toastId: btoa(params.message || ''),
    updateId: btoa(params.message || '')
  }
}

const DEFAULT_ACTION = {
  label: 'Dismiss'
}

export const toast = {
  info: (message: string, options: ToastOptions = {}) => {
    const { action = { label: 'Dismiss' }, shouldDeduplicate = false } = options || {}
    toastify.info(bodyParser({ message, action }), optionsParser({ message, shouldDeduplicate }))
  },
  error: (message: string, options: ToastOptions = {}) => {
    const { action = { label: 'Dismiss' }, shouldDeduplicate = false } = options || {}
    toastify.error(bodyParser({ message, action }), optionsParser({ message, shouldDeduplicate }))
  },
  success: (message: string, options: ToastOptions = {}) => {
    const { action = DEFAULT_ACTION, shouldDeduplicate = false } = options || {}
    toastify.success(bodyParser({ message, action }), optionsParser({ message, shouldDeduplicate }))
  },
  dismiss: toastify.dismiss
}

export const toastStyles = css`
  .Toastify__ {
    &toast {
      min-height: ${(p) => p.theme.av8.toast.height};
      max-width: 100%;
      color: ${(p) => p.theme.av8.typography.secondaryColor};
      background: ${(p) => p.theme.av8.background};

      &-container {
        width: fit-content;
        max-width: calc(100% - 1rem);
        margin: 0 0.5rem;
        top: 10rem;
        ${() => media.desktopUp} {
          max-width: calc(${() => breakpoints.desktop}px - 18rem);
          margin: 0;
        }
      }

      &-body {
        width: 100%;
      }

      &--success {
        color: ${(p) => p.theme.av8.successColor};
      }
      &--error {
        color: ${(p) => p.theme.av8.dangerColor};
      }
    }
    &progress-bar {
      // Don't hide the progress bar in order to make autoClose work.
      background-color: transparent;
      height: 0px;
    }
  }
`
